import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Zap, DollarSign, Bell, FileText, LayoutDashboard,
  ArrowRight, CheckCircle2, Shield, Eye, ChevronDown
} from 'lucide-react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { SeoHead } from '../components/SeoHead';
import { FeatureStickyNav } from '../components/features/FeatureStickyNav';
import {
  DashboardMockup, ForecastMockup, StressTestMockup,
  SubventionsMockup, AlertesMockup, ReportingMockup
} from '../components/features/ProductMockups';

interface FonctionnalitesPageProps {
  onNavigate: (path: string) => void;
  onSignIn: () => void;
  onSignUp: () => void;
}

const FEATURES = [
  {
    id: 'prevision',
    href: '/fonctionnalites/prevision-tresorerie',
    icon: TrendingUp,
    color: 'blue',
    title: 'Prévision de trésorerie',
    desc: 'Projetez votre trésorerie sur 12 mois. Visualisez les tensions à venir avant qu\'elles ne deviennent des crises.',
    points: ['Courbe de trésorerie projetée', 'Solde min/max/moyen', 'Vision à 30 / 60 / 90 jours'],
    mockup: <ForecastMockup />,
  },
  {
    id: 'stress-test',
    href: '/fonctionnalites/stress-test-tresorerie',
    icon: Zap,
    color: 'orange',
    title: 'Stress test de trésorerie',
    desc: 'Testez la résistance de votre structure face à un retard de subvention ou une baisse de revenus.',
    points: ['Temps de survie financière', 'Burn rate quotidien', 'Date de tension estimée'],
    mockup: <StressTestMockup />,
  },
  {
    id: 'subventions',
    href: '/fonctionnalites/suivi-subventions',
    icon: DollarSign,
    color: 'green',
    title: 'Suivi des subventions',
    desc: 'Suivez l\'état de chaque subvention et visualisez l\'impact des retards sur votre trésorerie.',
    points: ['Subventions attendues vs reçues', 'Alerte retard automatique', 'Impact sur la trésorerie'],
    mockup: <SubventionsMockup />,
  },
  {
    id: 'alertes',
    href: '/fonctionnalites/alertes-tresorerie',
    icon: Bell,
    color: 'red',
    title: 'Alertes de trésorerie',
    desc: 'Soyez alerté dès qu\'un seuil est franchi, qu\'un paiement est en retard ou qu\'un risque est détecté.',
    points: ['Alertes seuil personnalisables', 'Notifications retard de flux', 'Priorité par niveau de risque'],
    mockup: <AlertesMockup />,
  },
  {
    id: 'reporting',
    href: '/fonctionnalites/reporting-financier',
    icon: FileText,
    color: 'teal',
    title: 'Reporting financier',
    desc: 'Générez automatiquement vos rapports mensuels. Indicateurs clés, graphiques, export PDF en un clic.',
    points: ['Rapport mensuel automatique', 'KPI détaillés + graphiques', 'Export PDF clé en main'],
    mockup: <ReportingMockup />,
  },
  {
    id: 'dashboard',
    href: '/fonctionnalites/tableau-bord-temps-reel',
    icon: LayoutDashboard,
    color: 'slate',
    title: 'Tableau de bord temps réel',
    desc: 'Votre situation financière complète en un coup d\'œil. Soldes, flux, alertes, score de pilotage.',
    points: ['Vue synthétique instantanée', 'Encaissements / décaissements', 'Score de pilotage global'],
    mockup: <DashboardMockup />,
  },
];

const COLOR_MAP: Record<string, { icon: string; badge: string; border: string; btn: string }> = {
  blue:   { icon: 'text-blue-600',   badge: 'bg-blue-50 text-blue-700',   border: 'border-blue-200', btn: 'bg-blue-600 hover:bg-blue-700' },
  orange: { icon: 'text-orange-500', badge: 'bg-orange-50 text-orange-700', border: 'border-orange-200', btn: 'bg-orange-500 hover:bg-orange-600' },
  green:  { icon: 'text-green-600',  badge: 'bg-green-50 text-green-700',  border: 'border-green-200', btn: 'bg-green-600 hover:bg-green-700' },
  red:    { icon: 'text-red-500',    badge: 'bg-red-50 text-red-700',      border: 'border-red-200',   btn: 'bg-red-500 hover:bg-red-600' },
  teal:   { icon: 'text-teal-600',   badge: 'bg-teal-50 text-teal-700',    border: 'border-teal-200',  btn: 'bg-teal-600 hover:bg-teal-700' },
  slate:  { icon: 'text-slate-600',  badge: 'bg-slate-50 text-slate-700',  border: 'border-slate-200', btn: 'bg-slate-700 hover:bg-slate-800' },
};

const FAQ_ITEMS = [
  { q: 'TrezoOne remplace-t-il un expert-comptable ?', a: 'Non. TrezoOne est un outil de pilotage opérationnel de votre trésorerie. Votre expert-comptable reste indispensable pour la comptabilité légale et les conseils fiscaux.' },
  { q: 'Comment sont importées les transactions ?', a: 'Vous importez manuellement vos relevés bancaires (CSV, OFX, PDF). Aucune connexion bancaire automatique n\'est requise, ce qui garantit votre confidentialité totale.' },
  { q: 'Puis-je gérer plusieurs comptes bancaires ?', a: 'Oui, TrezoOne permet de gérer plusieurs comptes simultanément et d\'avoir une vue consolidée de votre trésorerie globale.' },
  { q: 'Comment fonctionnent les alertes ?', a: 'Vous configurez des seuils personnalisés. TrezoOne vous alerte automatiquement dès qu\'une condition est remplie : solde bas, retard de paiement, tension prévisionnelle.' },
  { q: 'Le stress test, c\'est quoi exactement ?', a: 'Le stress test simule des scénarios défavorables (retard de subvention, baisse de revenus) et calcule votre Temps de Survie Financière, votre Burn Rate, et la date de tension estimée.' },
];

export function FonctionnalitesPage({ onNavigate, onSignIn, onSignUp }: FonctionnalitesPageProps) {
  const navigate = useNavigate();
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  function nav(path: string) {
    if (onNavigate) { onNavigate(path); }
    else { window.scrollTo(0, 0); navigate(path); }
  }

  function scrollToSection(id: string) {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
      '@type': 'Question', name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <>
      <SeoHead
        title="Fonctionnalités TrezoOne — Pilotage de trésorerie pour associations et PME"
        description="Découvrez toutes les fonctionnalités TrezoOne : prévision de trésorerie, stress test, suivi des subventions, alertes, reporting financier et tableau de bord temps réel."
        canonical="/fonctionnalites"
        keywords="fonctionnalités gestion trésorerie, prévision trésorerie, stress test financier, suivi subventions, alertes trésorerie, reporting financier"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-white">
        <MarketingHeader onNavigate={nav} onSignIn={onSignIn} onSignUp={onSignUp} />
        <FeatureStickyNav onSectionClick={scrollToSection} />

        {/* HERO */}
        <section className="bg-gradient-to-b from-slate-50 to-white pt-14 pb-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold mb-7">
                  <Eye size={12} /> Hub produit
                </div>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight mb-5 tracking-tight">
                  Pilotez votre trésorerie avec plus de{' '}
                  <span className="text-blue-600">visibilité</span> et moins d'incertitude.
                </h1>
                <p className="text-lg text-gray-500 mb-8 leading-relaxed max-w-lg">
                  TrezoOne vous permet de prévoir votre trésorerie, tester des scénarios de risque, suivre vos financements et piloter vos flux financiers depuis un seul outil.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <button
                    onClick={() => nav('/beta-founders')}
                    className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-xl hover:bg-blue-700 font-bold shadow-lg transition-all"
                  >
                    Demander une démo <ArrowRight size={18} />
                  </button>
                  <button
                    onClick={() => onSignUp()}
                    className="inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-700 px-7 py-3.5 rounded-xl hover:border-blue-300 hover:text-blue-600 transition-all font-medium"
                  >
                    Essai gratuit 14j
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500">
                  {['Import manuel', 'Aucune connexion bancaire', 'Données privées'].map((t, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <Shield size={13} className="text-green-500" />
                      {t}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <DashboardMockup />
              </div>
            </div>
          </div>
        </section>

        {/* DIFFÉRENCES */}
        <section className="py-12 px-4 border-y border-gray-100 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                { icon: <Shield size={18} className="text-blue-600" />, title: 'Import manuel uniquement', desc: 'Importez vos relevés Excel ou PDF. Aucune connexion bancaire requise.' },
                { icon: <Eye size={18} className="text-blue-600" />, title: 'Zéro espionnage', desc: 'Aucun accès à vos comptes. Aucune revente de données. Aucun tracking.' },
                { icon: <CheckCircle2 size={18} className="text-blue-600" />, title: 'Vous gardez le contrôle', desc: 'Vos données vous appartiennent. Exportez-les quand vous voulez.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 p-5 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-9 h-9 bg-white border border-gray-100 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm mb-1">{item.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURES HUB */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">6 fonctionnalités clés</h2>
              <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
                Un outil complet conçu pour les associations, PME et structures qui ont besoin d'une vraie visibilité financière.
              </p>
            </div>

            <div className="space-y-16">
              {FEATURES.map((feature, index) => {
                const Icon = feature.icon;
                const colors = COLOR_MAP[feature.color];
                const isEven = index % 2 === 0;

                return (
                  <div
                    key={feature.id}
                    ref={el => { sectionRefs.current[feature.id] = el; }}
                    className="grid lg:grid-cols-2 gap-10 items-center scroll-mt-32"
                  >
                    <div className={isEven ? '' : 'lg:order-2'}>
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-5 border ${colors.badge} ${colors.border}`}>
                        <Icon size={12} /> {feature.title}
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{feature.title}</h2>
                      <p className="text-gray-500 mb-5 leading-relaxed">{feature.desc}</p>
                      <ul className="space-y-2.5 mb-7">
                        {feature.points.map((p, i) => (
                          <li key={i} className="flex items-center gap-2.5 text-sm text-gray-700">
                            <CheckCircle2 size={15} className="text-green-500 flex-shrink-0" />
                            {p}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => nav(feature.href)}
                        className={`inline-flex items-center gap-2 ${colors.btn} text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm`}
                      >
                        En savoir plus <ArrowRight size={14} />
                      </button>
                    </div>
                    <div className={`flex justify-center ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                      {feature.mockup}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* TARIFS LINK */}
        <section className="py-14 px-4 bg-gray-50 border-t border-gray-100">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-white border border-gray-200 rounded-2xl p-7 shadow-sm">
              <div>
                <div className="font-bold text-gray-900 mb-1">Nos packs tarifaires</div>
                <p className="text-sm text-gray-500">Toutes les fonctionnalités CORE incluses dès le Pack Essentiel à 49 €/mois.</p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <button onClick={() => nav('/tarifs')} className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 font-semibold text-sm transition-all">
                  Voir les tarifs <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Questions fréquentes</h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((item, i) => (
                <details key={i} className="border border-gray-200 rounded-xl overflow-hidden group">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer bg-white hover:bg-gray-50 transition-colors list-none">
                    <span className="font-semibold text-gray-900 text-sm pr-4">{item.q}</span>
                    <ChevronDown size={16} className="text-gray-400 flex-shrink-0 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-6 pb-5 pt-2 bg-white border-t border-gray-100">
                    <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-600 to-blue-800">
          <div className="max-w-xl mx-auto text-center text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Prêt à prendre le contrôle ?</h2>
            <p className="text-blue-100 mb-8 text-sm leading-relaxed">
              14 jours d'essai gratuit. Aucune carte bancaire. Aucun engagement.
            </p>
            <button
              onClick={() => nav('/beta-founders')}
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl hover:bg-blue-50 font-bold text-base shadow-xl transition-all"
            >
              Démarrer l'essai gratuit <ArrowRight size={18} />
            </button>
            <p className="text-blue-200 text-xs mt-4">14 jours d'essai · Sans engagement · Sans carte bancaire</p>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
